import { useEffect, useRef, useState } from "react";
import Turnstile from "@/components/Turnstile";
import { apiUrl } from "@/lib/api";
import { EMAIL } from "@/lib/contact";
import { BRIEF_PRESET } from "@/site/hooks";
import { useT } from "@/i18n";
import { en } from "@/i18n/en";

/** Valorile trimise la server. Rămân în engleză în toate limbile, fiindcă sunt
 *  contractul cu API-ul: /api/brief le validează pe listă închisă cu zod, iar o
 *  etichetă tradusă ajunsă în cerere ar fi respinsă cu 400.
 *
 *  Eticheta afișată vine din dicționar, valoarea din en — legate prin poziție,
 *  deci ordinea opțiunilor trebuie să rămână identică în toate limbile. */
const STEPS = en.brief.steps;

type State = "asking" | "email" | "sending" | "done" | "error";

/** Formularul de brief.
 *
 *  În prototip, butonul „Send brief" doar avansa la ecranul de mulțumire — nu
 *  citea emailul și nu trimitea nimic. Aici chiar trimite: POST /api/brief,
 *  cu Turnstile și validare pe server, prin același serviciu Resend care
 *  deservea raportul ROI. Fără asta, singurul drum de conversie al site-ului
 *  ar fi fost o machetă. */
export default function Brief() {
  const t = useT();
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [details, setDetails] = useState("");
  const [token, setToken] = useState<string | undefined>();
  const [state, setState] = useState<State>("asking");
  const [err, setErr] = useState<string | null>(null);
  /* Verificarea anti-bot poate să nu ruleze deloc: script blocat de o extensie,
     widget respins de Cloudflare, browser cu protecții agresive. Serverul cere
     tokenul în producție, deci fără el formularul nu are cum să trimită — iar
     asta ar lăsa vizitatorul complet fără cale de contact, pe singurul drum de
     conversie al site-ului. Când se întâmplă, oferim trimiterea pe email cu
     răspunsurile deja completate, ca lead-ul să nu se piardă. Nu slăbim
     verificarea pe server: cine nu are token nu trece prin API. */
  const [botFailed, setBotFailed] = useState(false);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current = matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  /* Întrebările din secțiunea „Where does it hurt?" răspund deja la primul pas.
     Formularul îl marchează ca ales și trece la buget, ca nimeni să nu răspundă
     de două ori la aceeași întrebare. */
  useEffect(() => {
    const onPreset = (e: Event) => {
      const kind = (e as CustomEvent<string>).detail;
      if (!STEPS[0].opts.includes(kind as never)) return;
      setPicked((p) => { const n = [...p]; n[0] = kind; return n; });
      setStep((s) => (s === 0 ? 1 : s));
      setState((s) => (s === "asking" ? "asking" : s));
    };
    window.addEventListener(BRIEF_PRESET, onPreset);
    return () => window.removeEventListener(BRIEF_PRESET, onPreset);
  }, []);

  const pick = (opt: string) => {
    const next = [...picked];
    next[step] = opt;
    setPicked(next);
    window.setTimeout(
      () => {
        if (step + 1 >= STEPS.length) setState("email");
        setStep((s) => s + 1);
      },
      reduced.current ? 0 : 210,
    );
  };

  /** Brieful ca email gata scris, pentru când verificarea anti-bot nu poate rula. */
  const mailtoHref = () => {
    const body = [
      `What we're building: ${picked[0] ?? "—"}`,
      `Start: ${picked[1] ?? "—"}`,
      "",
      details.trim(),
      "",
    ].join("\n");
    return `mailto:${EMAIL}?subject=${encodeURIComponent("Project brief")}&body=${encodeURIComponent(body)}`;
  };

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setErr(t.brief.badEmail);
      return;
    }
    // Fără token, serverul răspunde 403. Nu are rost drumul dus-întors: arătăm
    // direct calea alternativă.
    if (botFailed) {
      setErr(null);
      setState("email");
      return;
    }
    setErr(null);
    setState("sending");
    try {
      const res = await fetch(apiUrl("/api/brief"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: picked[0],
          start: picked[1],
          details: details.trim() || undefined,
          email,
          turnstileToken: token,
        }),
      });
      if (!res.ok) {
        // 403 = verificarea anti-bot respinsă pe server. Tot un blocaj pe care
        // vizitatorul nu-l poate rezolva, deci trece pe calea alternativă.
        if (res.status === 403) {
          setBotFailed(true);
          setState("email");
          setErr(null);
          return;
        }
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? t.brief.generic);
      }
      setState("done");
    } catch (e) {
      setErr(e instanceof Error ? e.message : t.brief.generic);
      setState("error");
    }
  };

  /** Eticheta tradusă a unei valori engleze. `picked` ține valorile de API, dar
   *  vizitatorul trebuie să-și vadă răspunsul în limba lui. */
  const label = (stepIdx: number, value?: string) => {
    if (!value) return value ?? "";
    const at = STEPS[stepIdx].opts.indexOf(value as never);
    return at < 0 ? value : t.brief.steps[stepIdx].opts[at];
  };

  const restart = () => {
    setStep(0); setPicked([]); setEmail(""); setDetails(""); setErr(null); setState("asking");
  };

  // Trei repere: cele două întrebări cu butoane, plus ecranul final.
  const filled = Math.min(step, 2);

  return (
    /* data-tb pune eticheta blocului călare pe muchia de sus, ca pe planșă. */
    <div className="brief" data-tb={t.brief.block}>
      <div
        className="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={2}
        aria-valuenow={filled}
      >
        {[0, 1, 2].map((i) => (
          <i key={i} className={i === filled ? "on now" : i < filled ? "on" : undefined} />
        ))}
      </div>
      <div className="stage">
        {state === "asking" && step < STEPS.length && (
          <div className="step">
            <div className="qline">
              <span className="qn">0{step + 1}</span>
              <span className="q">{t.brief.steps[step].q}</span>
            </div>
            <div className="opts">
              {STEPS[step].opts.map((o, oi) => (
                <button
                  key={o}
                  type="button"
                  className={"opt" + (picked[step] === o ? " on" : "")}
                  onClick={() => pick(o)}
                >
                  {t.brief.steps[step].opts[oi]}
                </button>
              ))}
            </div>
          </div>
        )}

        {(state === "email" || state === "sending" || state === "error") && (
          <div className="step">
            <div className="qline">
              <span className="qn">03</span>
              <span className="q">{t.brief.detailsQ}</span>
            </div>
            {/* Câmpul care lipsea. Fără el, un brief însemna trei butoane și o
                adresă — adică exact atât cât să nu pot răspunde cu ceva util.
                Rămâne opțional: cine vrea doar să deschidă o discuție nu e
                blocat de un câmp obligatoriu. */}
            <textarea
              className="briefdetails"
              rows={5}
              placeholder={t.brief.detailsPlaceholder}
              aria-label={t.brief.detailsQ}
              maxLength={4000}
              value={details}
              disabled={state === "sending"}
              onChange={(e) => setDetails(e.target.value)}
            />
            <p className="briefopt">{t.brief.detailsOptional}</p>
            <div className="mailrow">
              {/* Atributele de mai jos sunt pentru telefon, unde câmpul ăsta e
                  ultimul pas dintre cineva și un mesaj trimis.
                  `inputMode="email"` scoate tastatura cu @ și punct la vedere,
                  `autoComplete="email"` lasă telefonul să propună adresa
                  salvată — un tap în loc de treizeci de caractere — iar
                  `enterKeyHint="send"` face ca tasta mare din colț să scrie
                  „trimite", nu „mergi". `autoCapitalize` și `autoCorrect`
                  sunt oprite: iOS scria „Vitek@" cu majusculă și corecta
                  numele de domeniu. */}
              <input
                type="email"
                placeholder={t.brief.mailPlaceholder}
                aria-label="Email"
                inputMode="email"
                autoComplete="email"
                enterKeyHint="send"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                disabled={state === "sending"}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void send(); }}
              />
              {botFailed ? (
                <a className="act act-a" href={mailtoHref()}>{t.brief.sendByEmail}</a>
              ) : (
                <button
                  type="button"
                  className="act act-a"
                  disabled={state === "sending"}
                  onClick={() => void send()}
                >
                  {state === "sending" ? t.brief.sending : t.brief.send}
                </button>
              )}
            </div>
            <Turnstile onToken={setToken} onError={() => setBotFailed(true)} />
            <p className="briefhint">{picked.map((v, i) => label(i, v)).filter(Boolean).join("  ·  ")}</p>
            {botFailed && (
              <p className="brieferr">{t.brief.botFailed}</p>
            )}
            {err && <p className="brieferr">{err}</p>}
          </div>
        )}

        {state === "done" && (
          <div className="step">
            <div className="done">
              <span className="dot" /> {t.brief.doneText}
            </div>
            <button type="button" className="act act-b" onClick={restart}>
              {t.brief.another}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
